
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { Navigation, MapPin, Route, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'fricamp_location_disclosure_accepted';

interface LocationGateContextType {
  locationDisclosureAccepted: boolean;
  gatedGetCurrentPosition: (
    success: PositionCallback,
    error?: PositionErrorCallback | null,
    options?: PositionOptions
  ) => void;
  gatedWatchPosition: (
    success: PositionCallback,
    error?: PositionErrorCallback | null,
    options?: PositionOptions
  ) => number | undefined;
}

const LocationGateContext = createContext<LocationGateContextType>({
  locationDisclosureAccepted: true,
  gatedGetCurrentPosition: () => {},
  gatedWatchPosition: () => undefined,
});

export const useLocationGate = () => useContext(LocationGateContext);

export const LocationGateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const isNative = Capacitor.isNativePlatform();
  const [accepted, setAccepted] = useState(() => {
    if (!isNative) return true;
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });
  const [showModal, setShowModal] = useState(() => {
    // Show disclosure immediately on first launch on native
    if (!isNative) return false;
    return localStorage.getItem(STORAGE_KEY) !== 'true';
  });
  const declinedThisSession = useRef(false);

  const pendingRef = useRef<Array<{
    type: 'get' | 'watch';
    success: PositionCallback;
    error?: PositionErrorCallback | null;
    options?: PositionOptions;
  }>>([]);

  const flushPending = useCallback(() => {
    const queue = [...pendingRef.current];
    pendingRef.current = [];
    queue.forEach(({ type, success, error, options }) => {
      if (type === 'get') {
        navigator.geolocation.getCurrentPosition(success, error || undefined, options);
      } else {
        navigator.geolocation.watchPosition(success, error || undefined, options);
      }
    });
  }, []);

  const handleAccept = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setAccepted(true);
    setShowModal(false);
    flushPending();
  }, [flushPending]);

  const handleDecline = useCallback(() => {
    declinedThisSession.current = true;
    setShowModal(false);
    const queue = [...pendingRef.current];
    pendingRef.current = [];
    queue.forEach(({ error }) => {
      if (error) {
        error({ code: 1, message: 'User declined location disclosure', PERMISSION_DENIED: 1, POSITION_UNAVAILABLE: 2, TIMEOUT: 3 } as GeolocationPositionError);
      }
    });
  }, []);

  const gatedGetCurrentPosition = useCallback((
    success: PositionCallback,
    error?: PositionErrorCallback | null,
    options?: PositionOptions
  ) => {
    if (!navigator.geolocation) {
      error?.({ code: 2, message: 'Geolocation not available', PERMISSION_DENIED: 1, POSITION_UNAVAILABLE: 2, TIMEOUT: 3 } as GeolocationPositionError);
      return;
    }
    if (accepted || !isNative) {
      navigator.geolocation.getCurrentPosition(success, error || undefined, options);
      return;
    }
    if (declinedThisSession.current) {
      error?.({ code: 1, message: 'Location disclosure not accepted', PERMISSION_DENIED: 1, POSITION_UNAVAILABLE: 2, TIMEOUT: 3 } as GeolocationPositionError);
      return;
    }
    pendingRef.current.push({ type: 'get', success, error, options });
    setShowModal(true);
  }, [accepted, isNative]);

  const gatedWatchPosition = useCallback((
    success: PositionCallback,
    error?: PositionErrorCallback | null,
    options?: PositionOptions
  ): number | undefined => {
    if (!navigator.geolocation) return undefined;
    if (accepted || !isNative) {
      return navigator.geolocation.watchPosition(success, error || undefined, options);
    }
    if (declinedThisSession.current) return undefined;
    pendingRef.current.push({ type: 'watch', success, error, options });
    setShowModal(true);
    return undefined;
  }, [accepted, isNative]);

  return (
    <LocationGateContext.Provider value={{ locationDisclosureAccepted: accepted, gatedGetCurrentPosition, gatedWatchPosition }}>
      {/* On native: don't render ANY app content until disclosure is accepted.
          This guarantees no useEffect can trigger geolocation before disclosure. */}
      {(accepted || !isNative) ? children : null}

      {showModal && (
        <div className="fixed inset-0 z-[10080] flex flex-col items-center justify-center p-6 bg-black/95">
          {/* Google Play Prominent Disclosure requirement:
              Must be shown BEFORE any location permission is requested.
              Must clearly explain foreground AND background location usage.
              Must include link to privacy policy.
              Must have affirmative consent button. */}
          <div className="max-w-sm w-full space-y-5">
            {/* Icon */}
            <div className="flex justify-center">
              <div className="p-4 rounded-full bg-blue-500/15 border-2 border-blue-500/30">
                <Navigation size={36} className="text-blue-400" />
              </div>
            </div>

            {/* Title */}
            <h2 className="text-[20px] font-black text-white text-center leading-tight">
              {t('startup_location_disclosure.title', 'Fricamp needs your location')}
            </h2>

            {/* Foreground usage */}
            <div className="flex items-start gap-3 bg-white/5 rounded-xl p-3">
              <MapPin size={20} className="text-emerald-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-[12px] font-bold text-white mb-1">
                  {t('startup_location_disclosure.foreground_title', 'While using the app')}
                </p>
                <p className="text-[12px] text-stone-300 leading-relaxed">
                  {t('startup_location_disclosure.body', 'Fricamp uses your location to show camping and motorhome spots nearby and to provide driving directions.')}
                </p>
              </div>
            </div>

            {/* Background usage — MUST be explicit per Google Play policy */}
            <div className="flex items-start gap-3 bg-blue-500/10 rounded-xl p-3 border border-blue-500/20">
              <Route size={20} className="text-blue-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-[12px] font-bold text-white mb-1">
                  {t('startup_location_disclosure.background_title', 'In the background')}
                </p>
                <p className="text-[12px] text-stone-300 leading-relaxed">
                  {t('startup_location_disclosure.background', 'Fricamp collects location data in the background to enable GPS trip logging. This allows your trip to be recorded even when the screen is off or you are using other apps. Background location is only active when you explicitly start trip logging.')}
                </p>
              </div>
            </div>

            {/* Privacy — with link */}
            <div className="flex items-start gap-3">
              <Shield size={16} className="text-stone-500 mt-0.5 shrink-0" />
              <p className="text-tiny text-stone-500 leading-relaxed">
                {t('startup_location_disclosure.privacy', 'Your location data is stored locally on your device and is never shared with third parties. You can revoke location access at any time in your device settings.')}
                {' '}
                <a href="https://www.fricamp.com/personvern" className="text-blue-400 underline" target="_blank" rel="noopener noreferrer">
                  {t('startup_location_disclosure.privacy_link', 'Privacy Policy')}
                </a>
              </p>
            </div>

            {/* Buttons */}
            <div className="flex flex-col gap-3 pt-1">
              <button
                onClick={handleAccept}
                className="w-full py-4 rounded-2xl bg-emerald-600 text-white text-[13px] font-black uppercase tracking-widest hover:bg-emerald-500 transition-colors"
              >
                {t('startup_location_disclosure.accept', 'Allow location access')}
              </button>
              <button
                onClick={handleDecline}
                className="w-full py-3 rounded-2xl text-stone-500 text-[12px] font-bold"
              >
                {t('startup_location_disclosure.decline', 'Not now')}
              </button>
            </div>
          </div>
        </div>
      )}
    </LocationGateContext.Provider>
  );
};
